// /contact — Premium customer support page.
// Two-column layout: enquiry form (left) + contact cards (right).
// Reuses existing AddressAutocomplete and POST /api/leads with type=contact.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { submitLead, submitProspectCRM } from "@/lib/api";
import AddressAutocomplete from "@/components/forms/AddressAutocomplete";
import Reveal from "@/components/layout/Reveal";
import {
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  Sparkles,
  Calendar,
  ArrowRight,
  MessageSquare,
} from "lucide-react";

const ENQUIRY_TYPES = [
  { value: "general", label: "General enquiry" },
  { value: "product", label: "Product question" },
  { value: "installation_question", label: "Installation question" },
  { value: "warranty_support", label: "Warranty / support" },
  { value: "home_show", label: "Home Show enquiry" },
  { value: "finance_support", label: "Finance / green loan question" },
  { value: "other", label: "Other" },
];

const HOME_SHOW_REGIONS = [
  "Auckland", "Waikato", "Bay of Plenty", "Hawke's Bay",
  "Manawatū", "Wellington", "Canterbury", "Other",
];

// Known events — sourced from the Home Shows page data. Customers who pick
// "Other" get a free-text input fallback (handled inline below).
const HOME_SHOWS = [
  "Auckland Home Show",
  "Go Green Expo Auckland",
  "Waikato Home & Garden Show",
  "Hawke's Bay Home & Garden Show",
  "Home & Lifestyle Show Manawatū",
  "Wellington Home & Garden Show",
  "Go Green Expo Wellington",
  "Canterbury Home Show",
  "The Star Home Show Christchurch",
  "Go Green Expo Christchurch",
  "Other",
];

const VISIT_OPTIONS = ["Yes", "Maybe", "Not sure"];
const FOLLOWUP_OPTIONS = ["Email", "Phone"];

const EMAILS = [
  {
    addr: "sales@aeromaxenergy.co.nz",
    label: "Sales & free assessments",
  },
  {
    addr: "support@aeromaxenergy.co.nz",
    label: "Product support & customer help",
  },
  {
    addr: "accounts@aeromaxenergy.co.nz",
    label: "Accounts & invoices",
  },
];

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0B132B] text-white">
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[440px] w-[660px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-16 sm:pt-20 sm:pb-20">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/85">
            <MessageSquare className="h-3 w-3 text-[#10B981]" /> Contact AeroMax
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-heading mt-5 text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] max-w-4xl">
            Contact <span className="text-[#10B981]">AeroMax Energy</span>.
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl">
            Have a question about hot water heat pumps, finance options, home shows or installation? Our team is here to help.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ContactCards() {
  return (
    <div className="space-y-5">
      {/* Emails card */}
      <Card data-testid="contact-emails" className="rounded-2xl p-6 sm:p-7 border-[#E2E8F0] bg-white">
        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
            <Mail className="h-5 w-5" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0B132B]">Email us</h3>
        </div>
        <ul className="space-y-3">
          {EMAILS.map((e) => (
            <li key={e.addr} className="border-l-2 border-[#10B981]/40 pl-3">
              <a
                href={`mailto:${e.addr}`}
                className="text-sm font-semibold text-[#0B132B] hover:text-[#10B981] transition-colors break-all"
              >
                {e.addr}
              </a>
              <p className="text-xs text-[#475569] mt-0.5">{e.label}</p>
            </li>
          ))}
        </ul>
      </Card>

      {/* Phone card */}
      <Card data-testid="contact-phone" className="rounded-2xl p-6 sm:p-7 border-[#E2E8F0] bg-white">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
            <Phone className="h-5 w-5" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0B132B]">Call us</h3>
        </div>
        <a
          href="tel:0508237662"
          className="font-heading text-2xl font-bold text-[#0B132B] hover:text-[#10B981] transition-colors"
        >
          0508 AEROMAX
        </a>
        <p className="text-xs text-[#475569] mt-1">Monday–Friday, NZ business hours</p>
      </Card>

      {/* Office card */}
      <Card data-testid="contact-office" className="rounded-2xl p-6 sm:p-7 border-[#E2E8F0] bg-white">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#10B981]/10 text-[#10B981]">
            <MapPin className="h-5 w-5" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0B132B]">Office</h3>
        </div>
        <address className="not-italic text-sm text-[#0B132B] leading-relaxed">
          Building 4, Level 3,<br />
          61 Constellation Drive,<br />
          Rosedale, Auckland 0632,<br />
          New Zealand
        </address>
        <p className="mt-3 text-xs text-[#475569] leading-relaxed">
          Office visits by appointment only. We service homes across New Zealand through our assessment and installation partner network.
        </p>
      </Card>

      {/* CTA card */}
      <Card className="rounded-2xl p-6 sm:p-7 border-[#E2E8F0] bg-gradient-to-br from-white via-white to-[#F0FDF4]">
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B132B] text-[#10B981]">
            <Sparkles className="h-5 w-5" />
          </span>
          <h3 className="font-heading text-lg font-bold text-[#0B132B]">Prefer to estimate first?</h3>
        </div>
        <p className="text-sm text-[#475569] mt-1 leading-relaxed">
          See your savings potential in under 2 minutes before reaching out.
        </p>
        <Link
          to="/savings-calculator"
          data-testid="contact-cta-calculator"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-5 py-2.5 text-sm font-semibold transition-all"
        >
          Run Savings Calculator <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Card>
    </div>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    // `street` is the address line without suburb/city/postcode (CRM address).
    // suburb/city/postcode are auto-filled from the autocomplete but editable.
    address: "", street: "", suburb: "", city: "", region: "", postal_code: "", country: "",
    lat: null, lng: null,
    enquiry_type: "general",
    message: "",
    // Home Show enquiry conditional fields
    hs_region: "",
    hs_event: "",
    hs_event_other: "",
    hs_visiting: "",
    hs_followup: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isHomeShow = form.enquiry_type === "home_show";
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      return toast.error("Please enter your name and email so we can reply.");
    }
    setSubmitting(true);
    try {
      const enquiryLabel =
        ENQUIRY_TYPES.find((t) => t.value === form.enquiry_type)?.label || form.enquiry_type;

      // Compose notes — keep general message, and append Home Show metadata
      // when the customer picked the Home Show enquiry type.
      const noteLines = [`Enquiry type: ${enquiryLabel}`];
      if (isHomeShow) {
        const eventLabel =
          form.hs_event === "Other"
            ? `Other: ${form.hs_event_other || "—"}`
            : form.hs_event || "—";
        noteLines.push(
          `Region: ${form.hs_region || "—"}`,
          `Home show: ${eventLabel}`,
          `Planning to visit: ${form.hs_visiting || "—"}`,
          `Preferred follow-up: ${form.hs_followup || "—"}`,
        );
      }
      noteLines.push("", form.message || "(no message)");
      const notes = noteLines.join("\n");

      await submitLead({
        type: "contact",
        name: form.name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        // Backend lead unchanged: keep sending the locality here as before
        // (the new, distinct suburb/city go to the CRM only).
        suburb: form.city || form.suburb,
        region: isHomeShow && form.hs_region ? form.hs_region : form.region,
        postal_code: form.postal_code,
        country: form.country,
        latitude: form.lat,
        longitude: form.lng,
        consent: true,
        notes,
        internal_lead_data: isHomeShow
          ? {
              source: "Home Show Enquiry",
              hs_region: form.hs_region || null,
              hs_event:
                form.hs_event === "Other"
                  ? form.hs_event_other || "Other"
                  : form.hs_event || null,
              hs_visiting: form.hs_visiting || null,
              hs_followup: form.hs_followup || null,
            }
          : undefined,
      });

      // CRM prospect push — isolated & fire-and-forget so it can't affect the
      // existing enquiry flow.
      try {
        submitProspectCRM({
          name: form.name,
          email: form.email,
          phone: form.phone,
          // Street line only — suburb/city/postcode go as separate CRM fields.
          address: form.street || form.address,
          suburb: form.suburb,
          city: isHomeShow && form.hs_region ? form.hs_region : form.city,
          postcode: form.postal_code,
          message: notes,
          leadSource: "WEBSITE-LEAD-FORM",
        });
      } catch { /* never block the existing flow */ }

      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Submission failed. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Card data-testid="contact-success" className="rounded-3xl p-10 border-[#E2E8F0] bg-white text-center">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#10B981]/10 text-[#10B981] mb-5">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-[#0B132B]">Thank you</h2>
        <p className="mt-3 text-[#475569] leading-relaxed">
          AeroMax Energy has received your enquiry. We will review your details and respond shortly.
        </p>
      </Card>
    );
  }

  return (
    <Card data-testid="contact-form" className="rounded-3xl p-6 sm:p-8 border-[#E2E8F0] bg-white">
      <form onSubmit={submit} className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <FormField label="Full name *">
            <Input data-testid="c-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </FormField>
          <FormField label="Email *">
            <Input data-testid="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
          </FormField>
          <FormField label="Phone">
            <Input data-testid="c-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </FormField>
          <FormField label="Address">
            <AddressAutocomplete
              data-testid="c-address"
              value={form.address}
              onChange={(v) => set("address", v)}
              onPlaceSelected={(p) => {
                setForm((s) => ({
                  ...s,
                  address: p.formatted_address || s.address,
                  street: p.street_address || s.street,
                  suburb: p.sublocality || p.locality || s.suburb,
                  city: p.locality || s.city,
                  region: p.region || s.region,
                  postal_code: p.postal_code || s.postal_code,
                  country: p.country || s.country,
                  lat: p.lat ?? s.lat,
                  lng: p.lng ?? s.lng,
                }));
              }}
              placeholder="Start typing your NZ address…"
              autoComplete="off"
            />
          </FormField>
          <FormField label="Suburb">
            <Input data-testid="c-suburb" value={form.suburb} onChange={(e) => set("suburb", e.target.value)} placeholder="Suburb" />
          </FormField>
          <FormField label="City">
            <Input data-testid="c-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City / town" />
          </FormField>
          <FormField label="Postcode">
            <Input data-testid="c-postcode" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="Postcode" />
          </FormField>
        </div>

        <FormField label="Enquiry type *">
          <select
            data-testid="c-enquiry-type"
            value={form.enquiry_type}
            onChange={(e) => set("enquiry_type", e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
          >
            {ENQUIRY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </FormField>

        {isHomeShow && (
          <div
            data-testid="c-home-show-block"
            className="rounded-2xl border border-[#10B981]/25 bg-[#F0FDF4]/40 p-5 sm:p-6 space-y-4"
          >
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#059669]">
              Home Show details
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <FormField label="Region">
                <select
                  data-testid="c-hs-region"
                  value={form.hs_region}
                  onChange={(e) => set("hs_region", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                >
                  <option value="">Select a region…</option>
                  {HOME_SHOW_REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Which home show are you interested in?">
                <select
                  data-testid="c-hs-event"
                  value={form.hs_event}
                  onChange={(e) => set("hs_event", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                >
                  <option value="">Select an event…</option>
                  {HOME_SHOWS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </FormField>
              {form.hs_event === "Other" && (
                <div className="sm:col-span-2">
                  <FormField label="Tell us which one">
                    <Input
                      data-testid="c-hs-event-other"
                      value={form.hs_event_other}
                      onChange={(e) => set("hs_event_other", e.target.value)}
                      placeholder="Event name"
                    />
                  </FormField>
                </div>
              )}
              <FormField label="Are you planning to visit us?">
                <select
                  data-testid="c-hs-visiting"
                  value={form.hs_visiting}
                  onChange={(e) => set("hs_visiting", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                >
                  <option value="">Select…</option>
                  {VISIT_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Preferred follow-up">
                <select
                  data-testid="c-hs-followup"
                  value={form.hs_followup}
                  onChange={(e) => set("hs_followup", e.target.value)}
                  className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                >
                  <option value="">Select…</option>
                  {FOLLOWUP_OPTIONS.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </FormField>
            </div>
          </div>
        )}

        <FormField label="Message">
          <textarea
            data-testid="c-message"
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            rows={5}
            className="w-full border border-[#E2E8F0] rounded-md px-3 py-2 bg-white text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] resize-none"
            placeholder="Tell us a little about your home or your question…"
          />
        </FormField>

        <Button
          data-testid="c-submit"
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-6 font-semibold text-base"
        >
          {submitting ? "Sending…" : "Send Enquiry"}
        </Button>

        <p className="text-[11px] text-[#475569] leading-relaxed text-center">
          Your information is safe with us. We will not share your details with third parties without your consent.
        </p>
      </form>
    </Card>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#0B132B]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-white">
      <Hero />
      <section className="py-14 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-bold tracking-[0.18em] uppercase">
              <Calendar className="h-3 w-3" /> Send an enquiry
            </span>
            <h2 className="font-heading mt-3 text-2xl sm:text-3xl font-bold text-[#0B132B] leading-tight">
              Tell us a little about your home.
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[#475569] leading-relaxed max-w-xl">
              We'll get back to you with the next step — whether that's a free assessment, a savings discussion, or finance prep.
            </p>
            <div className="mt-6">
              <EnquiryForm />
            </div>
          </div>
          <div className="lg:sticky lg:top-24">
            <ContactCards />
          </div>
        </div>
      </section>
    </div>
  );
}
